import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import ReactMarkdown from 'react-markdown';
import {
  MessageSquare, Users, Activity, Calendar, Search,
  ChevronRight, Trash2, X, User, Bot, Clock,
  ThumbsUp, ThumbsDown, BookOpen
} from 'lucide-react';
import { API } from '../../lib/apiBase';

const AnalyticsDashboard = () => {
  const [overview, setOverview] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionDetail, setSessionDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const loadOverview = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/analytics/overview`, { withCredentials: true });
      setOverview(data);
    } catch (error) {
      console.error('Error loading overview:', error);
    }
  }, []);

  const loadSessions = useCallback(async (q = '') => {
    setLoadingSessions(true);
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (q) params.append('search', q);
      const { data } = await axios.get(`${API}/admin/analytics/sessions?${params}`, { withCredentials: true });
      setSessions(data.sessions);
    } catch (error) {
      console.error('Error loading sessions:', error);
    } finally {
      setLoadingSessions(false);
    }
  }, []);

  useEffect(() => {
    loadOverview();
    loadSessions();
  }, [loadOverview, loadSessions]);

  const handleSearch = (e) => {
    e.preventDefault();
    loadSessions(search);
  };

  const openSession = async (sessionId) => {
    setSelectedSession(sessionId);
    setLoadingDetail(true);
    try {
      const { data } = await axios.get(`${API}/admin/analytics/sessions/${sessionId}`, { withCredentials: true });
      setSessionDetail(data);
    } catch (error) {
      console.error('Error loading session:', error);
    } finally {
      setLoadingDetail(false);
    }
  };

  const submitFeedback = async (messageId, rating) => {
    try {
      await axios.post(
        `${API}/admin/feedback/${messageId}`,
        { rating },
        { withCredentials: true }
      );
      // Update local state optimistically
      setSessionDetail((prev) => ({
        ...prev,
        messages: prev.messages.map((m) =>
          m.id === messageId ? { ...m, feedback: rating } : m
        ),
      }));
      loadOverview(); // refresh stats
    } catch (error) {
      console.error('Error submitting feedback:', error);
    }
  };

  const clearFeedback = async (messageId) => {
    try {
      await axios.delete(`${API}/admin/feedback/${messageId}`, { withCredentials: true });
      setSessionDetail((prev) => ({
        ...prev,
        messages: prev.messages.map((m) =>
          m.id === messageId ? { ...m, feedback: null } : m
        ),
      }));
      loadOverview();
    } catch (error) {
      console.error('Error clearing feedback:', error);
    }
  };

  const closeSession = () => {
    setSelectedSession(null);
    setSessionDetail(null);
  };

  const deleteSession = async (sessionId) => {
    if (!window.confirm('Bu sohbet oturumunu silmek istediğinizden emin misiniz?')) return;
    try {
      await axios.delete(`${API}/admin/analytics/sessions/${sessionId}`, { withCredentials: true });
      closeSession();
      loadOverview();
      loadSessions(search);
    } catch (error) {
      console.error('Error deleting session:', error);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    return d.toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' });
  };

  const StatCard = ({ icon: Icon, label, value, accent }) => (
    <div className="border border-border bg-card p-5 flex items-start gap-4">
      <div className={`w-10 h-10 flex items-center justify-center ${accent || 'bg-primary/10 text-primary'} flex-shrink-0`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-1">{label}</p>
        <p className="text-3xl font-mono font-bold tracking-tight" data-testid={`stat-${label}`}>
          {value !== null && value !== undefined ? value.toLocaleString('tr-TR') : '-'}
        </p>
      </div>
    </div>
  );

  return (
    <div data-testid="analytics-dashboard">
      {/* Overview Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-8">
        <StatCard icon={Users} label="Oturum" value={overview?.total_sessions} />
        <StatCard icon={MessageSquare} label="Toplam Soru" value={overview?.total_user_messages} accent="bg-success/10 text-success" />
        <StatCard icon={Calendar} label="Bugün" value={overview?.today_user_messages} accent="bg-warning/10 text-foreground" />
        <StatCard icon={Activity} label="Son 7 Gün" value={overview?.week_user_messages} accent="bg-destructive/10 text-destructive" />
        <StatCard icon={ThumbsUp} label="Beğenilen" value={overview?.feedback_up} accent="bg-success/10 text-success" />
        <StatCard icon={BookOpen} label="Bilgi Notu" value={overview?.knowledge_count} accent="bg-primary/10 text-primary" />
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="mb-6 flex gap-2" data-testid="sessions-search-form">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Soru içeriğinde ara (örn: BMW, Toyota...)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="rounded-sm pl-9"
            data-testid="sessions-search-input"
          />
        </div>
        <Button type="submit" className="rounded-sm bg-primary" data-testid="sessions-search-button">Ara</Button>
        {search && (
          <Button type="button" variant="outline" className="rounded-sm" onClick={() => { setSearch(''); loadSessions(''); }}>
            Temizle
          </Button>
        )}
      </form>

      {/* Sessions List */}
      <div className="border border-border bg-card">
        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
          <h3 className="font-heading font-semibold">Sohbet Oturumları</h3>
          <span className="text-xs text-muted-foreground font-mono">{sessions.length} sonuç</span>
        </div>

        {loadingSessions ? (
          <div className="p-8 text-center text-muted-foreground">Yükleniyor...</div>
        ) : sessions.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">Henüz sohbet oturumu yok.</div>
        ) : (
          <div className="divide-y divide-border" data-testid="sessions-list">
            {sessions.map((s) => (
              <button
                key={s.session_id}
                onClick={() => openSession(s.session_id)}
                className="w-full text-left p-5 hover:bg-secondary/40 transition-colors flex items-start gap-4 group"
                data-testid={`session-row-${s.session_id}`}
              >
                <div className="w-9 h-9 rounded-sm bg-primary/10 flex items-center justify-center flex-shrink-0 mt-1">
                  <MessageSquare className="w-4 h-4 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium leading-snug line-clamp-2 group-hover:text-primary transition-colors">
                    {s.first_message || '(boş)'}
                  </p>
                  <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-1 font-mono">
                      <Clock className="w-3 h-3" />
                      {formatDate(s.last_timestamp)}
                    </span>
                    <span className="inline-flex items-center gap-1 font-mono">
                      <MessageSquare className="w-3 h-3" />
                      {s.message_count} mesaj
                    </span>
                    <span className="font-mono text-[10px] opacity-50">#{s.session_id.slice(0, 8)}</span>
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary flex-shrink-0 mt-3" />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Session Detail Modal */}
      {selectedSession && (
        <div
          className="fixed inset-0 z-50 bg-foreground/50 flex items-center justify-center p-4"
          onClick={closeSession}
          data-testid="session-detail-modal"
        >
          <div
            className="bg-background border border-border max-w-3xl w-full max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-border flex items-center justify-between sticky top-0 bg-background">
              <div>
                <h3 className="font-heading font-bold">Sohbet Detayı</h3>
                <p className="text-xs text-muted-foreground font-mono mt-0.5">#{selectedSession}</p>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => deleteSession(selectedSession)}
                  className="rounded-sm"
                  data-testid="delete-session-button"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Sil
                </Button>
                <Button size="sm" variant="ghost" onClick={closeSession} className="rounded-sm" data-testid="close-session-button">
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div className="overflow-y-auto p-6 space-y-5 flex-1">
              {loadingDetail ? (
                <p className="text-center text-muted-foreground">Yükleniyor...</p>
              ) : (
                sessionDetail?.messages?.map((msg, idx) => (
                  <div key={msg.id || `${msg.timestamp}-${idx}`} className="flex gap-3" data-testid={`detail-message-${idx}`}>
                    <div className={`w-7 h-7 flex items-center justify-center flex-shrink-0 ${
                      msg.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-secondary text-foreground'
                    }`}>
                      {msg.role === 'user' ? <User className="w-3.5 h-3.5" /> : <Bot className="w-3.5 h-3.5" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-xs font-semibold uppercase tracking-wider">
                          {msg.role === 'user' ? 'Kullanıcı' : 'Volt Usta'}
                        </span>
                        <span className="text-xs text-muted-foreground font-mono">{formatDate(msg.timestamp)}</span>
                        {msg.role === 'assistant' && msg.id && (
                          <div className="ml-auto flex items-center gap-1">
                            <button
                              onClick={() => msg.feedback === 'up' ? clearFeedback(msg.id) : submitFeedback(msg.id, 'up')}
                              className={`p-1.5 border transition-colors ${
                                msg.feedback === 'up'
                                  ? 'bg-success text-white border-success'
                                  : 'border-border hover:border-success hover:text-success'
                              }`}
                              title="Bu yanıt iyi"
                              data-testid={`feedback-up-${msg.id}`}
                            >
                              <ThumbsUp className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => msg.feedback === 'down' ? clearFeedback(msg.id) : submitFeedback(msg.id, 'down')}
                              className={`p-1.5 border transition-colors ${
                                msg.feedback === 'down'
                                  ? 'bg-destructive text-white border-destructive'
                                  : 'border-border hover:border-destructive hover:text-destructive'
                              }`}
                              title="Bu yanıt kötü"
                              data-testid={`feedback-down-${msg.id}`}
                            >
                              <ThumbsDown className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        )}
                      </div>
                      {msg.role === 'user' ? (
                        <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                      ) : (
                        <div className="prose prose-sm prose-neutral max-w-none text-sm leading-relaxed">
                          <ReactMarkdown
                            components={{
                              h1: ({ children }) => <h4 className="font-heading font-bold mt-3 mb-1.5 text-base">{children}</h4>,
                              h2: ({ children }) => <h4 className="font-heading font-bold mt-3 mb-1.5 text-base">{children}</h4>,
                              h3: ({ children }) => <h5 className="font-heading font-semibold mt-2 mb-1 text-sm">{children}</h5>,
                              p: ({ children }) => <p className="mb-2">{children}</p>,
                              ul: ({ children }) => <ul className="list-disc list-outside ml-5 mb-2 space-y-0.5">{children}</ul>,
                              ol: ({ children }) => <ol className="list-decimal list-outside ml-5 mb-2 space-y-0.5">{children}</ol>,
                              strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                              a: ({ children, href }) => <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary underline">{children}</a>,
                            }}
                          >
                            {msg.content}
                          </ReactMarkdown>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalyticsDashboard;
